import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import { runFeedPreview } from "../../hooks/GAFeedLabHooks";
import {
  getFeedRankingConfig,
  updateFeedRankingConfig,
} from "../../../GAListenFeedSettings/hooks/ListenFeedSettingsHook";

/**
 * FeedLabPanel
 *
 * Admin tool (General Admin → QuickTales → Feed Lab) to exercise the in-house
 * Listen-feed ranker (`previewFeedRanking`) that replaces AddSearch. It ranks the
 * live `searchIndex` candidate pool and shows each item's total score plus a
 * per-component breakdown, so we can validate/tune ranking before flipping
 * `useInternalFeed`.
 *
 * Weight overrides here are "what-if" only — they are passed to the endpoint per
 * request and are NOT saved to config/feedRanking (edit those on the
 * "Listen Feed & Search Settings" page).
 *
 * See docs/ADDSEARCH_REPLACEMENT_PLAN.md (Phase 3 / Feed Lab).
 */
const WEIGHT_KEYS = [
  "recency",
  "popularity",
  "engagement",
  "affinity",
  "orgPriority",
  "qtPriority",
  "nearby",
];

// Friendly display labels for the weight override fields.
const WEIGHT_LABELS = {
  recency: "Recency",
  popularity: "Popularity",
  engagement: "Engagement",
  affinity: "Affinity",
  orgPriority: "Org Priority",
  qtPriority: "QT Priority",
  nearby: "Nearby",
};

const FeedLabPanel = () => {
  const [limit, setLimit] = useState(25);
  const [uid, setUid] = useState("");
  const [lat, setLat] = useState("");
  const [long, setLong] = useState("");
  // Empty string = "use saved config value" for that weight.
  const [weightOverrides, setWeightOverrides] = useState({});
  // Saved weight values (config/feedRanking) shown next to each field label.
  const [savedWeights, setSavedWeights] = useState(null);

  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [applying, setApplying] = useState(false);

  // Fetches the saved config weights so we can show current values next to each
  // override field label. Returns the weights (or null on failure).
  const loadSavedWeights = async () => {
    try {
      const cfg = await getFeedRankingConfig();
      const weights = cfg?.weights || null;
      setSavedWeights(weights);
      return weights;
    } catch (e) {
      setSavedWeights(null);
      return null;
    }
  };

  // Load saved config weights on mount so we can show current values next to
  // each override field label.
  useEffect(() => {
    loadSavedWeights();
  }, []);

  const handleWeightChange = (key, value) => {
    setWeightOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const buildConfigOverride = () => {
    const weights = {};
    WEIGHT_KEYS.forEach((k) => {
      const v = weightOverrides[k];
      if (v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v))) {
        weights[k] = Number(v);
      }
    });
    return Object.keys(weights).length ? { weights } : undefined;
  };

  const handleRun = async () => {
    setLoading(true);
    setError("");
    try {
      const nearby =
        lat !== "" && long !== ""
          ? { lat: Number(lat), long: Number(long) }
          : undefined;
      const data = await runFeedPreview({
        limit: Number(limit) || 25,
        configOverride: buildConfigOverride(),
        uid: uid.trim() || undefined,
        nearby,
      });
      setResults(data.results || []);
      setMeta({
        poolSize: data.poolSize || 0,
        poolCap: data.poolCap || 0,
        environment: data.environment || "",
        personalized: !!data.personalized,
        nearbyApplied: !!data.nearbyApplied,
        tookMs: data.tookMs || 0,
      });
    } catch (e) {
      console.error("Feed Lab preview failed:", e);
      setError(e.message || "Feed preview failed");
      setResults([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setWeightOverrides({});
    setUid("");
    setLat("");
    setLong("");
    setLimit(25);
  };

  // Collects the weight override fields that the user actually changed (blank =
  // untouched). Returns a { weights } payload or null if nothing changed.
  const buildWeightUpdate = () => {
    const weights = {};
    WEIGHT_KEYS.forEach((k) => {
      const v = weightOverrides[k];
      if (v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v))) {
        weights[k] = Number(v);
      }
    });
    return Object.keys(weights).length ? { weights } : null;
  };

  // Saves any changed override fields to config/feedRanking, refreshes the
  // saved values shown in the labels, then clears the override fields.
  const handleApply = async () => {
    const update = buildWeightUpdate();
    if (!update) return;
    setApplying(true);
    setError("");
    try {
      await updateFeedRankingConfig({ ...update, updatedAt: new Date() });
      await loadSavedWeights();
      setWeightOverrides({});
    } catch (e) {
      console.error("Feed Lab apply settings failed:", e);
      setError(e.message || "Failed to save settings");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#fafafa",
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        Feed Lab (in-house Listen feed ranker)
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#666", mb: 2 }}>
        Ranks the live <code>searchIndex</code> candidate pool with the internal
        ranker that replaces AddSearch. Weight overrides below are “what-if” only
        and are not saved — edit the persisted values on the “Listen Feed &amp;
        Search Settings” page.
      </Typography>

      {/* Run controls */}
      <Grid container spacing={2} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Results"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Viewer uid (optional)"
            placeholder="for affinity personalization"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2} md={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Lat (optional)"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2} md={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Long (optional)"
            value={long}
            onChange={(e) => setLong(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Weight overrides */}
      <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 1, mb: 1 }}>
        Weight overrides (blank = use saved value)
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {WEIGHT_KEYS.map((key) => {
          const savedVal = savedWeights ? savedWeights[key] : undefined;
          const labelText = WEIGHT_LABELS[key] ?? key;
          return (
            <Box key={key} sx={{ flex: 1, minWidth: 120 }}>
              {/* Field name always shown above the field, with current saved
                  value in parentheses (e.g. "Recency (3.1)"). */}
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "#333" }}
              >
                {labelText}
                {savedVal != null && ` (${savedVal})`}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={weightOverrides[key] ?? ""}
                inputProps={{ min: 0, step: 0.1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow clearing (revert to saved value); otherwise clamp to >= 0.
                  if (raw === "") {
                    handleWeightChange(key, "");
                  } else {
                    handleWeightChange(key, String(Math.max(0, Number(raw))));
                  }
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Ranking…" : "Run Feed Preview"}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleApply}
          disabled={loading || applying || !buildWeightUpdate()}
          sx={{ textTransform: "none" }}
        >
          {applying ? "Applying…" : "Apply Settings"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={loading || applying}
          sx={{ textTransform: "none" }}
        >
          Reset
        </Button>
        {(loading || applying) && <CircularProgress size={20} />}
      </Box>

      {error && (
        <Typography sx={{ mt: 1.5, fontSize: 13, color: "#c62828" }}>
          {error}
        </Typography>
      )}

      {!error && meta && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: "#888" }}>
          {results.length} shown / {meta.poolSize} in pool (cap {meta.poolCap}) ·{" "}
          {meta.environment} · {meta.personalized ? "personalized" : "generic"} ·{" "}
          {meta.nearbyApplied ? "nearby on" : "nearby off"} · {meta.tookMs}ms
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        {results.map((r, idx) => (
          <Box
            key={`${r.docType}-${r.id}`}
            sx={{
              py: 1,
              px: 1.5,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            {/* Column 1 — rank number */}
            <Typography
              sx={{ fontSize: 16, color: "#aaa", width: 24, flexShrink: 0 }}
            >
              {idx + 1}
            </Typography>

            {/* Column 2 — image */}
            <Box
              sx={{
                width: 86,
                height: 86,
                flexShrink: 0,
                borderRadius: 1,
                backgroundColor: "#f0f0f0",
              }}
            >
              {r.imageUrl && (
                <Box
                  component="img"
                  src={r.imageUrl}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 86,
                    height: 86,
                    borderRadius: 1,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>

            {/* Column 3 — text / stats / breakdown */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {r.title || "(untitled)"}
                </Typography>
                <Chip
                  label={r.docType}
                  size="small"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Box>
              <Typography sx={{ fontSize: 15, color: "#1f1f1f" }}>
                {[
                  `Org: ${r.orgName || r.orgHandle || 'None'}`,
                                  ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
              <Typography sx={{ fontSize: 15, color: "#1f1f1f" }}>
                {[
                  r.narratorName && `Told By: ${r.narratorName}`,
                  r.createdByHandle && `Created By: @${r.createdByHandle}`,
                  r.popScore != null && `Popularity Score: ${r.popScore}`,
                  `#Listens: ${r.listenCount}`,
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
              {r.breakdown && (
                <Typography sx={{ fontSize: 14, color: "#454545" }}>
                  {Object.entries(r.breakdown)
                    .map(
                      ([k, v]) =>
                        `${WEIGHT_LABELS[k] ?? k}: ${Number(v).toFixed(2)}`
                    )
                    .join("  ·  ")}
                </Typography>
              )}
            </Box>

            {/* Column 4 — score (right justified) */}
            <Typography
              sx={{
                fontSize: 15,
                color: "#1f1f1f",
                flexShrink: 0,
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              score {typeof r.score === "number" ? r.score.toFixed(3) : "-"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FeedLabPanel;

